import { useEffect, useRef, useState } from 'react'
import { useIsFetching, useIsMutating, useQuery } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { getSessionEpoch } from '@/lib/auth'
import { PERMISSION_KEYS } from '@/lib/permissions'
import { usePermissionsAt } from '@/lib/use-permissions'
import {
  policyCatalogKeys,
  policyOrderKeys,
  PolicySelector,
  PRINTER_SELECTOR_FIELDS,
  usePolicyOrder,
  type SelectorGui,
} from '@/modules/hierarchy'
import { Button } from '@/ui/button'
import { DialogTitle } from '@/ui/dialog'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { TextField } from '@/ui/text-field'
import { getAlertCapabilities } from '../../api/alert-rules-api'
import {
  eventRulePoliciesApi,
  PRINTER_EVENT_RULE_POLICY_TYPE,
  type EventRuleContent,
  type EventRulePolicyEdit,
  type PrinterEventRulePolicy,
} from '../../api/event-rule-policies-api'
import { compareEventRulePolicy, resolveEventRulePolicy, type EventRuleChoices, type EventRuleComparison } from '../../lib/printer-policy-comparison'
import { PrinterPolicyConflictReview, PrinterPolicyDraftCopy } from '../../lib/printer-policy-conflict-review'
import {
  builderCatalog,
  type BuilderCatalog,
  type ConditionNode,
} from '../alert-rules/alert-condition'
import { EventConditionFields } from './event-condition-fields'
import {
  consumableDocument,
  defaultConsumableCondition,
  readConsumableCondition,
} from './event-condition-model'
import { EventNotificationActions } from './event-notification-actions'
import {
  actionContent,
  actionDraft,
  MAX_NOTIFICATION_RECIPIENTS,
  newEventRuleId,
  workflowDraft,
} from './event-action-model'
import { useSaveEventRulePolicy } from './event-rule-policy-queries'
import { usePrinterPolicyReconciliation } from '../printer-policies/use-printer-policy-reconciliation'

type FormProps = {
  entityId: string
  policy: PrinterEventRulePolicy | null
  onClose: () => void
  onCreateLockChange?: (locked: boolean) => void
  onPendingChange?: (pending: boolean) => void
  onReload?: () => void
  reloading?: boolean
  reloadError?: string | null
}

export function EventRulePolicyForm(props: FormProps) {
  const capabilities = useQuery({
    queryKey: [
      'assets',
      'alert-capabilities',
      getSessionEpoch(),
      props.entityId,
    ],
    queryFn: ({ signal }) => getAlertCapabilities(props.entityId, signal),
  })
  if (!capabilities.data)
    return capabilities.isError ? (
      <RetryNotice
        message="Event conditions could not be loaded."
        onRetry={() => void capabilities.refetch()}
      />
    ) : (
      <LoadingNotice message="Loading event conditions…" />
    )
  const catalog = builderCatalog(capabilities.data)
  if (
    !catalog ||
    catalog.assetType !== 'printer' ||
    catalog.event !== 'consumable.updated' ||
    !catalog.fields.some((field) => field.id === 'supply.percentRemaining')
  ) {
    return (
      <p role="alert" className="text-xs">
        Printer consumable conditions are not available.
      </p>
    )
  }
  return <EventRulePolicyFields {...props} catalog={catalog} />
}

function editingRestriction(
  actionsRedacted: boolean,
  emailLocked: boolean,
  unsupportedActions: boolean,
) {
  if (actionsRedacted)
    return 'Action details are hidden. Editing requires permission to manage event rules.'
  if (emailLocked)
    return 'Editing this rule requires email-recipient permission.'
  if (unsupportedActions)
    return 'This rule contains actions this editor cannot change. Its saved content is preserved.'
  return null
}

export function EventRulePolicyFields({
  entityId,
  policy,
  onClose,
  onCreateLockChange,
  onPendingChange,
  onReload,
  reloading = false,
  reloadError,
  catalog,
}: FormProps & { catalog: BuilderCatalog }) {
  const permissions = usePermissionsAt(entityId)
  const canAddEmail = permissions.can(PERMISSION_KEYS.alertEmailManage)
  const canManage = permissions.can(PERMISSION_KEYS.alertManage)
  const canRead = permissions.can(PERMISSION_KEYS.alertRead)
  const canEdit = canManage && canRead
  const currentGrants = useRef({ canEdit, canAddEmail })
  useEffect(() => { currentGrants.current = { canEdit, canAddEmail } }, [canEdit, canAddEmail])
  const nameInput = useRef<HTMLInputElement>(null)
  const [baseline, setBaseline] = useState(policy)
  const [latest, setLatest] = useState<PrinterEventRulePolicy | null>(null)
  const [comparison, setComparison] = useState<EventRuleComparison | null>(null)
  const [choices, setChoices] = useState<EventRuleChoices>({})
  const [needsReview, setNeedsReview] = useState(false)
  const [reviewVisible, setReviewVisible] = useState(false)
  const [repeated, setRepeated] = useState(false)
  const [loadingLatest, setLoadingLatest] = useState(false)
  const [latestError, setLatestError] = useState<string | null>(null)
  const [failedBaseline, setFailedBaseline] = useState<PrinterEventRulePolicy | null>(null)
  const [replacementVersion, setReplacementVersion] = useState(0)
  const [initialContent, setInitialContent] = useState(policy?.rule.content ?? null)
  const save = useSaveEventRulePolicy(entityId)
  const reconciliation = usePrinterPolicyReconciliation(entityId, PRINTER_EVENT_RULE_POLICY_TYPE)
  const order = usePolicyOrder(
    entityId,
    PRINTER_EVENT_RULE_POLICY_TYPE,
    policy === null,
  )
  const saving = useIsMutating({ mutationKey: ['printer-policy-save', entityId, PRINTER_EVENT_RULE_POLICY_TYPE] })
  const deleting = useIsMutating({ mutationKey: ['printer-policy-delete', entityId, PRINTER_EVENT_RULE_POLICY_TYPE] })
  const moving = useIsMutating({ mutationKey: ['policy-move', entityId, PRINTER_EVENT_RULE_POLICY_TYPE] })
  const toggling = useIsMutating({ mutationKey: ['policy-enabled', entityId, PRINTER_EVENT_RULE_POLICY_TYPE] })
  const refreshingCatalog = useIsFetching({ queryKey: policyCatalogKeys.all(entityId) })
  const refreshingOrder = useIsFetching({ queryKey: policyOrderKeys.type(entityId, PRINTER_EVENT_RULE_POLICY_TYPE) })
  const [operationId] = useState(() => crypto.randomUUID())
  const [ruleId] = useState(newEventRuleId)
  const [name, setName] = useState(policy?.name ?? '')
  const [initialCondition, setInitialCondition] = useState(() => {
    if (!policy) return defaultConsumableCondition()
    if (policy.rule.content.detectorType !== 'printer.consumable.updated')
      return null
    return readConsumableCondition(catalog, policy.rule.content.configuration)
  })
  const [condition, setCondition] = useState(initialCondition)
  const [actions, setActions] = useState(() =>
    (policy?.rule.content.actions ?? []).map(actionDraft),
  )
  const [workflows, setWorkflows] = useState(() =>
    (policy?.workflows ?? []).map(workflowDraft),
  )
  const [selectorExpression, setSelectorExpression] = useState(
    policy?.selectorExpression ?? '',
  )
  const [selectorGui, setSelectorGui] = useState<unknown>(
    policy?.selectorGui ?? null,
  )
  const [selectorValid, setSelectorValid] = useState(true)
  const [attemptedCreate, setAttemptedCreate] =
    useState<EventRulePolicyEdit | null>(null)
  const [uncertain, setUncertain] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const unsupportedActions = hasUnsupportedActions(actionContent(actions))
  const showActions = !unsupportedActions && (policy !== null || Boolean(condition?.threshold.field))
  const restriction = contentRestriction(latest ?? baseline, actionContent(actions))
  const protectedContent = restriction !== null
  const reviewRestricted = savedRestriction(latest ?? baseline) !== null
    || (baseline !== null && latest !== null && linkedWorkflowsChanged(baseline, latest))
  const pending = save.isPending || reloading || loadingLatest
  useEffect(() => {
    onPendingChange?.(pending)
    return () => onPendingChange?.(false)
  }, [pending, onPendingChange])
  const writeBlocked = pending || saving > 0 || deleting > 0 || moving > 0 || toggling > 0
    || refreshingCatalog > 0 || refreshingOrder > 0
  const locked =
    pending || uncertain || !canEdit || protectedContent || reviewVisible
  useEffect(() => {
    if (policy === null) onCreateLockChange?.(save.isPending || reloading || uncertain)
  }, [policy, save.isPending, reloading, uncertain, onCreateLockChange])
  const orderReady =
    policy !== null || (order.data !== undefined && !order.isFetching && !order.isError)
  const orderConflict =
    !policy &&
    !uncertain &&
    save.error instanceof ApiError &&
    save.error.code === 'POLICY_ORDER_CONFLICT'
  const updateConflict =
    policy !== null &&
    save.error instanceof ApiError &&
    save.error.status === 409
  let saveErrorMessage = save.error?.message
  if (orderConflict) saveErrorMessage = 'This policy or its order changed. Reload it before saving again.'
  if (updateConflict) saveErrorMessage = 'The save conflicts with the current policy or another operation. Reload it before saving again.'
  const title = policy ? `Edit: ${policy.name}` : 'Define the event rule'
  let saveLabel = 'Save policy'
  if (uncertain) saveLabel = 'Retry same policy'
  if (save.isPending) saveLabel = 'Saving…'
  if (needsReview) saveLabel = 'Review changes'
  const selectedWorkflowIds = workflows.flatMap(({ workflow }) => workflow ? [workflow.definitionId] : [])
  const workflowDefinitionIds = selectedWorkflowIds.length || baseline?.workflows?.length
    ? selectedWorkflowIds : undefined
  const resolvedFields = comparison ? resolveEventRulePolicy(comparison, choices) : null
  const resolved = resolvedFields ? { ...resolvedFields, workflowDefinitionIds } : null
  const resolvedRestriction = resolved ? contentRestriction(comparison?.latest ?? null, resolved.content.actions) : null
  const draftText = JSON.stringify({
    name, selectorExpression, selectorGui,
    condition: condition ? {
      detectorType: 'printer.consumable.updated',
      threshold: rawCondition(condition.threshold),
      filters: condition.filters.map(rawCondition),
      filterOperator: condition.filterOperator,
    } : { detectorType: initialContent?.detectorType, configuration: initialContent?.configuration },
    actions: actionContent(actions),
    workflowDefinitionIds,
  }, null, 2)

  useEffect(() => {
    if (!reviewVisible && (needsReview || repeated)) nameInput.current?.focus()
  }, [reviewVisible, needsReview, repeated])

  function currentContent(): EventRuleContent | null {
    const serialized = condition ? consumableDocument(catalog, condition) : null
    const notifications = actionContent(actions)
    if (serialized) {
      return {
        detectorType: 'printer.consumable.updated',
        configuration: initialContent && condition === initialCondition
          ? initialContent.configuration
          : serialized.document,
        actions: notifications,
      }
    }
    return initialContent ? { ...initialContent, actions: notifications } : null
  }

  function currentEdit(): EventRulePolicyEdit | null {
    const content = currentContent()
    if (!content) return null
    return {
      name: name.trim(),
      content,
      selectorExpression: selectorExpression.trim() ? selectorExpression : null,
      selectorGui: selectorExpression.trim() ? selectorGui : null,
      overridesPolicyId: baseline?.overridesPolicyId ?? null,
      workflowDefinitionIds,
      ...(policy === null && order.data
        ? {
            placement: {
              atStart: false,
              beforePolicyId: null,
              expectedOrder: order.data.map(({ id, revision }) => ({
                id,
                revision,
              })),
            },
          }
        : {}),
    }
  }

  function candidateCondition(edit: EventRulePolicyEdit) {
    const draftContent = currentContent()
    if (draftContent?.detectorType === edit.content.detectorType
      && JSON.stringify(draftContent.configuration) === JSON.stringify(edit.content.configuration)) return condition
    if (edit.content.detectorType !== 'printer.consumable.updated') return null
    return readConsumableCondition(catalog, edit.content.configuration)
  }

  function editProblem(edit: EventRulePolicyEdit) {
    const expression = edit.selectorExpression ?? ''
    if (!edit.name.trim() || edit.name.trim().length > 200 || expression.length > 4096
      || (expression === selectorExpression && !selectorValid)) return 'Enter a policy name and complete the target selector.'
    const candidate = candidateCondition(edit)
    if (candidate && consumableDocument(catalog, candidate).problems.size > 0) return 'Complete every event condition and supply filter.'
    if (edit.content.actions.some((action) => action.recipients.length === 0 || action.recipients.length > MAX_NOTIFICATION_RECIPIENTS)) {
      return `Choose between 1 and ${MAX_NOTIFICATION_RECIPIENTS} recipients for each notification action.`
    }
    return null
  }

  function buildEdit(candidate = currentEdit()) {
    if (!candidate) return null
    if (workflows.some(({ workflow }) => workflow === null)) {
      setLocalError('Choose a workflow for each workflow action.')
      return null
    }
    const problem = editProblem(candidate)
    if (problem) { setLocalError(problem); return null }
    return candidate
  }

  function contentRestriction(saved: PrinterEventRulePolicy | null, candidate: EventRuleContent['actions'], emailAllowed = canAddEmail) {
    return savedRestriction(saved, emailAllowed)
      ?? editingRestriction(false, !emailAllowed && usesEmail(candidate), hasUnsupportedActions(candidate))
  }

  function savedRestriction(saved: PrinterEventRulePolicy | null, emailAllowed = canAddEmail) {
    const savedActions = saved?.rule.content.actions ?? []
    return editingRestriction(saved?.actionsRedacted ?? false,
      !emailAllowed && usesEmail(savedActions), hasUnsupportedActions(savedActions))
  }

  function applyEdit(edit: EventRulePolicyEdit, saved: PrinterEventRulePolicy) {
    const nextCondition = candidateCondition(edit)
    setName(edit.name)
    setInitialContent(structuredClone(edit.content))
    setCondition(nextCondition)
    setInitialCondition(nextCondition)
    setActions(edit.content.actions.map(actionDraft))
    if (edit.workflowDefinitionIds === undefined) {
      setWorkflows((saved.workflows ?? []).map(workflowDraft))
    }
    setSelectorExpression(edit.selectorExpression ?? '')
    setSelectorGui(edit.selectorGui ?? null)
    setSelectorValid((edit.selectorExpression ?? '') !== selectorExpression || selectorValid)
    setReplacementVersion((version) => version + 1)
    setBaseline(saved)
    setNeedsReview(false)
    setRepeated(true)
    setReviewVisible(false)
    setLocalError(null)
  }

  function linkedWorkflowsChanged(opened: PrinterEventRulePolicy, saved: PrinterEventRulePolicy) {
    const ids = (rule: PrinterEventRulePolicy) => (rule.workflows ?? []).map(({ definitionId }) => definitionId)
    return JSON.stringify(ids(opened)) !== JSON.stringify(ids(saved))
  }

  async function fetchLatest(opened: PrinterEventRulePolicy, draft: EventRulePolicyEdit) {
    setNeedsReview(true)
    setReviewVisible(false)
    setLatest(null)
    setComparison(null)
    setChoices({})
    setLatestError(null)
    setFailedBaseline(opened)
    setUncertain(false)
    if (!currentGrants.current.canEdit) {
      setLatestError('You no longer have permission to read and manage this event rule. Your edits are kept.')
      return
    }
    setLoadingLatest(true)
    try {
      const saved = await eventRulePoliciesApi.get(entityId, opened.policyId)
      const grants = currentGrants.current
      if (!grants.canEdit) {
        setLatestError('You no longer have permission to read and manage this event rule. Your edits are kept.')
        return
      }
      setLatest(saved)
      if (saved.policyRevision > opened.policyRevision) {
        await reconciliation.reload()
        if (!currentGrants.current.canEdit) {
          setLatestError('You no longer have permission to read and manage this event rule. Your edits are kept.')
          return
        }
      }
      if (linkedWorkflowsChanged(opened, saved)) {
        setLatestError('Linked workflows changed. Copy your draft and reopen this rule before saving.')
        return
      }
      const blocked = savedRestriction(saved, currentGrants.current.canAddEmail)
      if (blocked) { setLatestError(blocked); return }
      setBaseline(opened)
      setComparison(compareEventRulePolicy(opened, draft, saved))
      setReviewVisible(true)
      save.reset()
    } catch {
      setLatestError('Couldn’t load the latest event rule. Your edits are kept. Retry loading before saving.')
    } finally {
      setLoadingLatest(false)
    }
  }

  function reviewCurrentDraft() {
    const draft = currentEdit()
    if (writeBlocked || !baseline || !latest || !draft || !canEdit) return
    if (linkedWorkflowsChanged(baseline, latest)) return
    const blocked = savedRestriction(latest)
    if (blocked) { setLatestError(blocked); return }
    setComparison(compareEventRulePolicy(baseline, draft, latest))
    setChoices({})
    setLatestError(null)
    setLocalError(null)
    setReviewVisible(true)
  }

  function update(saved: PrinterEventRulePolicy, candidate: EventRulePolicyEdit) {
    const grants = currentGrants.current
    if (writeBlocked || uncertain || !grants.canEdit || contentRestriction(saved, candidate.content.actions, grants.canAddEmail)) return
    const edit = buildEdit(candidate)
    if (!edit) return
    setLocalError(null)
    save.mutate({ policy: saved, operationId, ruleId, edit }, {
      onSuccess: onClose,
      onError: (error) => {
        if (error instanceof ApiError && error.code === 'PRINTER_EVENT_RULE_REVISION_CONFLICT') void fetchLatest(saved, edit)
        else handleSaveError(error)
      },
    })
  }

  function handleSaveError(error: unknown) {
    const rejected =
      error instanceof ApiError &&
      [400, 401, 403, 404, 409, 422].includes(error.status)
    if (policy !== null) {
      if (!rejected || (error instanceof ApiError && error.status === 409))
        setUncertain(true)
      return
    }
    const confirmsNoCommittedCreate =
      error instanceof ApiError && error.code === 'POLICY_ORDER_CONFLICT'
    const identityConflict =
      error instanceof ApiError &&
      error.code === 'PRINTER_EVENT_RULE_IDEMPOTENCY_CONFLICT'
    if (
      rejected &&
      !identityConflict &&
      (!uncertain || confirmsNoCommittedCreate)
    ) {
      setAttemptedCreate(null)
      setUncertain(false)
      return
    }
    setUncertain(true)
  }

  function submit() {
    if (writeBlocked || !currentGrants.current.canEdit) return
    if (needsReview) { reviewCurrentDraft(); return }
    if (
      save.isPending ||
      reloading ||
      protectedContent ||
      !canEdit ||
      !orderReady
    )
      return
    const edit = attemptedCreate ?? buildEdit()
    if (!edit) return
    if (baseline) { update(baseline, edit); return }
    setLocalError(null)
    if (policy === null) setAttemptedCreate(edit)
    save.mutate(
      { policy, operationId, ruleId, edit },
      { onSuccess: onClose, onError: handleSaveError },
    )
  }

  async function reloadOrder() {
    if (writeBlocked) return
    const refreshed = await order.refetch()
    if (!refreshed.isSuccess) return
    save.reset()
  }

  function changeSelector(
    expression: string,
    valid: boolean,
    gui: SelectorGui | null,
  ) {
    setSelectorExpression(expression)
    setSelectorValid(valid)
    setSelectorGui(gui)
  }
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div hidden={reviewVisible}>
        <form
          className="flex flex-col gap-6"
          aria-label={
            policy ? `Edit: ${policy.name}` : 'Create printer event rule policy'
          }
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <section className="flex flex-col gap-3">
            {policy ? (
              <DialogTitle>{title}</DialogTitle>
            ) : (
              <h2 className="text-sm font-medium">{title}</h2>
            )}
            <TextField
              ref={nameInput}
              label="Policy name"
              name="name"
              kind="text"
              onBlur={() => {}}
              value={name}
              maxLength={200}
              required
              disabled={locked}
              onChange={setName}
            />
            {protectedContent && (
              <p role="alert" className="text-xs">
                {restriction}
              </p>
            )}
            <div className="flex min-w-0 flex-col gap-4">
              {condition ? (
                <EventConditionFields
                  catalog={catalog}
                  value={condition}
                  onChange={setCondition}
                  disabled={locked}
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  This rule uses an advanced condition. The saved condition will
                  be preserved when you edit its name, targets, or notifications.
                </p>
              )}
              {showActions && (
                <EventNotificationActions
                  entityId={entityId}
                  value={actions}
                  onChange={setActions}
                  workflows={workflows}
                  onWorkflowsChange={setWorkflows}
                  disabled={locked}
                  canAddEmail={canAddEmail}
                />
              )}
            </div>
          </section>
          <PolicySelector
            entityId={entityId}
            value={selectorExpression}
            gui={selectorGui}
            targetFields={PRINTER_SELECTOR_FIELDS}
            targetLabel="printers"
            disabled={locked}
            replacementVersion={replacementVersion}
            onChange={changeSelector}
          />
          {policy === null && (
            <section className="flex flex-col gap-2">
              <h2 className="text-sm font-medium">Policy order</h2>
              <p className="text-xs text-muted-foreground">
                New policies are added after existing printer event rules. Every
                matching event rule applies.
              </p>
              {order.isError ? (
                <RetryNotice
                  message="Policy order could not be loaded."
                  disabled={writeBlocked}
                  onRetry={() => { if (!writeBlocked) void order.refetch() }}
                />
              ) : !order.data ? (
                <LoadingNotice message="Loading policy order…" />
              ) : null}
            </section>
          )}
          {!needsReview && (localError || save.error || reloadError) && (
            <p role="alert" className="text-xs text-destructive">
              {localError ?? reloadError ?? saveErrorMessage}
            </p>
          )}
          {loadingLatest && <LoadingNotice message="Loading the latest saved event rule…" />}
          {latestError && <RetryNotice message={latestError} disabled={writeBlocked || !canEdit} onRetry={() => {
            if (writeBlocked) return
            const draft = currentEdit()
            if (failedBaseline && draft) void fetchLatest(failedBaseline, draft)
          }} />}
          {needsReview && <>
            <p role="status" className="text-muted-foreground text-xs">Your edits are kept. Review the latest saved event rule before saving.</p>
            <PrinterPolicyDraftCopy draftText={draftText} />
          </>}
          {uncertain && (
            <p role="alert" className="text-xs">
              The save could not be confirmed.{' '}
              {policy
                ? 'Reload the policy to check its saved state.'
                : 'Retry the same policy before making further changes.'}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              disabled={pending}
              onClick={onClose}
            >
              Cancel
            </Button>
            {orderConflict && (
              <Button
                type="button"
                variant="outline"
                disabled={order.isFetching || writeBlocked}
                onClick={() => void reloadOrder()}
              >
                Reload policy order
              </Button>
            )}
            {policy && onReload && uncertain && !needsReview && (
              <Button
                type="button"
                variant="outline"
                disabled={writeBlocked}
                onClick={() => { if (!writeBlocked) onReload() }}
              >
                Reload policy
              </Button>
            )}
            <Button
              type={needsReview ? 'button' : 'submit'}
              onClick={needsReview ? reviewCurrentDraft : undefined}
              disabled={
                writeBlocked ||
                !orderReady ||
                (needsReview ? reviewRestricted : protectedContent) ||
                !canEdit ||
                (needsReview && latest === null) ||
                (uncertain && policy !== null)
              }
            >
              {saveLabel}
            </Button>
          </div>
        </form>
      </div>
      {reviewVisible && comparison && <>
        {resolvedRestriction && <p role="alert" className="text-destructive text-xs">{resolvedRestriction}</p>}
        <PrinterPolicyConflictReview kind="event" comparison={comparison} choices={choices}
          onChoose={(field, choice) => setChoices({ ...choices, [field]: choice })}
          draftText={draftText} repeated={repeated} disabled={pending || !canEdit}
          resolveDisabled={writeBlocked || resolvedRestriction !== null}
          saveDisabled={resolved === null || editProblem(resolved) !== null}
          onContinue={() => setReviewVisible(false)}
          onEditMerged={() => {
            if (!writeBlocked && resolved && canEdit && !contentRestriction(comparison.latest, resolved.content.actions)) applyEdit(resolved, comparison.latest)
          }}
          onSaveResolved={() => {
            if (writeBlocked || !resolved || !canEdit || contentRestriction(comparison.latest, resolved.content.actions)) return
            const edit = buildEdit(resolved)
            if (!edit) return
            applyEdit(edit, comparison.latest)
            update(comparison.latest, edit)
          }} />
      </>}
    </div>
  )
}

function usesEmail(actions: EventRuleContent['actions']) {
  return actions.some((action) => action.recipients.some((recipient) => recipient.type === 'email'))
}

function hasUnsupportedActions(actions: EventRuleContent['actions']) {
  return actions.some((action) => action.type !== 'notification'
    || action.recipients.some((recipient) => !['user', 'group', 'email'].includes(recipient.type)))
}

function rawCondition({ field, op, value }: ConditionNode) {
  return { field, op, value }
}
